import { useState, useEffect, useMemo, useRef } from "react";
import useUsers from "admin/hooks/useUsers";
import User from "admin/types/interfaces/user";
import { getUserInitials } from "admin/utils/users";
import { Search } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "~/components/ui/command";
import { Input } from "~/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import useToast from "~/components/ui/toast";
import { useDebounce } from "~/hooks/useDebounce";

const UserAutocomplete = ({
  handleSelectUser,
  selectedUsers,
  autoRefocus = false,
}: {
  handleSelectUser: (user: User) => void;
  selectedUsers: Array<Pick<User, "email">>;
  autoRefocus?: boolean;
}) => {
  const { showError } = useToast();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const debouncedQuery = useDebounce(userSearchTerm.trim());
  const {
    data = [],
    isLoading,
    error: searchError,
  } = useUsers({ searchQuery: debouncedQuery, activeOnly: true });

  const searchResults = useMemo(() => {
    if (!data || data.length === 0) {
      return [];
    }
    return data.filter((user) => !selectedUsers.some((u) => u.email === user.email));
  }, [data, selectedUsers]);

  useEffect(() => {
    if (!isLoading && searchError) {
      showError("Unable to retrieve search results");
    }
  }, [searchError, isLoading]);

  useEffect(() => {
    if (userSearchTerm.length > 0) {
      setIsPopoverOpen(true);
    } else {
      setIsPopoverOpen(false);
    }
  }, [userSearchTerm]);

  return (
    <div className="space-y-2 relative">
      <Popover open={isPopoverOpen}>
        <PopoverTrigger className="w-full">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <Input
              ref={inputRef}
              placeholder="Add users by name or email"
              value={userSearchTerm}
              onChange={(e) => {
                setUserSearchTerm(e.target.value);
              }}
              className="pl-10"
            />
          </div>
        </PopoverTrigger>
        <PopoverContent
          className="w-[var(--radix-popover-trigger-width)] p-0 bg-background border border-border shadow-lg"
          align="start"
          side="bottom"
          sideOffset={4}
          onWheel={(e) => {
            e.stopPropagation();
          }}
          onTouchMove={(e) => {
            e.stopPropagation();
          }}
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Command className="w-full">
            <CommandList className="max-h-[400px] bg-background overflow-y-auto">
              {isLoading || (userSearchTerm.length > 0 && !debouncedQuery) ? (
                <div className="py-6 text-center text-sm text-muted-foreground bg-background">
                  <div className="flex items-center justify-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-border border-t-foreground"></div>
                    Loading users...
                  </div>
                </div>
              ) : searchResults.length === 0 ? (
                <CommandEmpty className="bg-background">
                  No users found matching your search.
                </CommandEmpty>
              ) : (
                <CommandGroup className="bg-background">
                  {searchResults.map((user) => (
                    <CommandItem
                      key={user.email}
                      value={`${user.full_name} ${user.email}`}
                      className="p-0"
                      onSelect={() => {
                        handleSelectUser(user);
                        setUserSearchTerm("");
                        if (autoRefocus) {
                          inputRef.current?.focus();
                        }
                      }}
                    >
                      <div className="flex items-center gap-3 cursor-pointer bg-background hover:bg-accent hover:text-accent-foreground transition-colors px-3 py-2 w-full">
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.image_url} />
                          <AvatarFallback className="text-xs">
                            {getUserInitials(user)}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate">
                            {user.full_name || user.email}
                          </div>
                          <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                        </div>
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default UserAutocomplete;
